import { useState, useEffect } from "react";
import { 
  FolderKanban, 
  Plus, 
  Search,
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  Trash2,
  Edit,
  DollarSign
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectTypes = [
  { value: "music_production", label: "Music Production", icon: "🎵" },
  { value: "music_video", label: "Music Video", icon: "🎬" },
  { value: "photo_video_shoot", label: "Photo/Video Shoot", icon: "📸" },
  { value: "tattoo_appointment", label: "Tattoo Appointment", icon: "🎨" },
  { value: "brand_content", label: "Brand Content", icon: "✨" }
];

const statusOptions = [
  { value: "not_started", label: "Not Started", color: "#71717A" },
  { value: "active", label: "Active", color: "#D4AF37" },
  { value: "waiting", label: "Waiting", color: "#F59E0B" },
  { value: "completed", label: "Completed", color: "#22C55E" }
];

const Projects = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [projectForm, setProjectForm] = useState({
    title: "",
    project_type: "music_production",
    client_id: "",
    client_name: "",
    deadline: "",
    status: "not_started",
    deliverables: [],
    deposit: 0,
    remaining_balance: 0,
    total_value: 0,
    notes: ""
  });

  const [newDeliverable, setNewDeliverable] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes] = await Promise.all([
        fetch(`${API}/projects`, { credentials: "include" }),
        fetch(`${API}/clients`, { credentials: "include" })
      ]);

      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(data?.projects ?? data ?? []);
      } else {
        setProjects([]);
      }
      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data?.clients ?? data ?? []);
      } else {
        setClients([]);
      }
    } catch (error) {
      toast.error("Failed to load data");
      setProjects([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = async () => {
    try {
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(projectForm)
      });

      if (response.ok) {
        toast.success("Project created successfully");
        setShowAddDialog(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;
    try {
      const response = await fetch(`${API}/projects/${selectedProject.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(projectForm)
      });

      if (response.ok) {
        toast.success("Project updated");
        setShowEditDialog(false);
        setSelectedProject(null);
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to update project");
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await fetch(`${API}/projects/${id}`, {
        method: "DELETE",
        credentials: "include"
      });
      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setProjectForm({
      title: "",
      project_type: "music_production",
      client_id: "",
      client_name: "",
      deadline: "",
      status: "not_started",
      deliverables: [],
      deposit: 0,
      remaining_balance: 0,
      total_value: 0,
      notes: ""
    });
    setNewDeliverable("");
  };

  const openEditDialog = (project) => {
    setSelectedProject(project);
    setProjectForm({
      title: project.title,
      project_type: project.project_type,
      client_id: project.client_id || "",
      client_name: project.client_name || "",
      deadline: project.deadline || "",
      status: project.status,
      deliverables: project.deliverables || [],
      deposit: project.deposit || 0,
      remaining_balance: project.remaining_balance || 0,
      total_value: project.total_value || 0,
      notes: project.notes || ""
    });
    setShowEditDialog(true);
  };

  const addDeliverable = () => {
    if (!newDeliverable.trim()) return;
    setProjectForm({
      ...projectForm,
      deliverables: [...projectForm.deliverables, { name: newDeliverable, completed: false }]
    });
    setNewDeliverable("");
  };

  const toggleDeliverable = (index) => {
    const newDeliverables = [...projectForm.deliverables];
    newDeliverables[index].completed = !newDeliverables[index].completed;
    setProjectForm({ ...projectForm, deliverables: newDeliverables });
  };

  const removeDeliverable = (index) => {
    const newDeliverables = (projectForm.deliverables || []).filter((_, i) => i !== index);
    setProjectForm({ ...projectForm, deliverables: newDeliverables });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getProjectProgress = (project) => {
    if (!project.deliverables || project.deliverables.length === 0) return 0;
    const completed = (project.deliverables || []).filter(d => d.completed).length;
    return Math.round((completed / project.deliverables.length) * 100);
  };

  const getStatusColor = (status) => statusOptions.find(s => s.value === status)?.color || "#71717A";
  const getTypeIcon = (type) => projectTypes.find(t => t.value === type)?.icon || "📋";

  const filteredProjects = (projects || []).filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || p.project_type === filterType;
    const matchesStatus = filterStatus === "all" || p.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Projects</h1>
          <p className="text-[#A1A1AA] mt-1">Manage all your projects across divisions</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="add-project-btn">
              <Plus size={18} className="mr-2" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-[#27272A] max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-[#FAFAFA]">Create New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label className="text-[#A1A1AA]">Project Title *</Label>
                <Input
                  value={projectForm.title}
                  onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                  placeholder="Project name"
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Project Type</Label>
                  <Select value={projectForm.project_type} onValueChange={(v) => setProjectForm({ ...projectForm, project_type: v })}>
                    <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                      {projectTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Client</Label>
                  <Select 
                    value={projectForm.client_id} 
                    onValueChange={(v) => {
                      const client = clients.find(c => c.id === v);
                      setProjectForm({ ...projectForm, client_id: v, client_name: client?.name || "" });
                    }}
                  >
                    <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Deadline</Label>
                  <Input
                    type="date"
                    value={projectForm.deadline}
                    onChange={(e) => setProjectForm({ ...projectForm, deadline: e.target.value })}
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Status</Label>
                  <Select value={projectForm.status} onValueChange={(v) => setProjectForm({ ...projectForm, status: v })}>
                    <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                      {statusOptions.map((status) => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Total Value</Label>
                  <Input
                    type="number"
                    value={projectForm.total_value}
                    onChange={(e) => setProjectForm({ ...projectForm, total_value: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Deposit</Label>
                  <Input
                    type="number"
                    value={projectForm.deposit}
                    onChange={(e) => setProjectForm({ ...projectForm, deposit: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Balance</Label>
                  <Input
                    type="number"
                    value={projectForm.remaining_balance}
                    onChange={(e) => setProjectForm({ ...projectForm, remaining_balance: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Deliverables</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newDeliverable}
                    onChange={(e) => setNewDeliverable(e.target.value)}
                    placeholder="Add deliverable"
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    onKeyPress={(e) => e.key === 'Enter' && addDeliverable()}
                  />
                  <Button onClick={addDeliverable} variant="outline" className="border-[#27272A]">
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="mt-3 space-y-2">
                  {projectForm.deliverables.map((d, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-[#1A1A1D] rounded-lg">
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleDeliverable(idx)}>
                          {d.completed ? (
                            <CheckCircle2 size={18} className="text-[#22C55E]" />
                          ) : (
                            <Circle size={18} className="text-[#71717A]" />
                          )}
                        </button>
                        <span className={d.completed ? "text-[#71717A] line-through" : "text-[#FAFAFA]"}>
                          {d.name}
                        </span>
                      </div>
                      <button onClick={() => removeDeliverable(idx)} className="text-[#EF4444]">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Notes</Label>
                <Textarea
                  value={projectForm.notes}
                  onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })}
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <Button onClick={handleAddProject} className="w-full btn-gold">
                Create Project
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search projects..."
            className="pl-10 bg-[#121214] border-[#27272A] text-[#FAFAFA]"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48 bg-[#121214] border-[#27272A] text-[#FAFAFA]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
            <SelectItem value="all">All Types</SelectItem>
            {projectTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-[#121214] border-[#27272A] text-[#FAFAFA]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
            <SelectItem value="all">All Status</SelectItem>
            {statusOptions.map((status) => (
              <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.map((project) => (
          <Card 
            key={project.id} 
            className="bg-[#121214] border-[#27272A] card-hover cursor-pointer"
            onClick={() => openEditDialog(project)}
            data-testid={`project-card-${project.id}`}
          >
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getTypeIcon(project.project_type)}</span>
                  <div>
                    <h3 className="font-semibold text-[#FAFAFA]">{project.title}</h3>
                    <p className="text-xs text-[#71717A]">{project.client_name || "No client"}</p>
                  </div>
                </div>
                <span 
                  className="text-xs px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: `${getStatusColor(project.status)}20`,
                    color: getStatusColor(project.status)
                  }}
                >
                  {statusOptions.find(s => s.value === project.status)?.label}
                </span>
              </div>

              {project.deadline && (
                <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-3">
                  <Calendar size={14} />
                  <span>Due: {project.deadline}</span>
                </div>
              )}

              {project.deliverables?.length > 0 && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-[#71717A] mb-1">
                    <span>Progress</span>
                    <span>{getProjectProgress(project)}%</span>
                  </div>
                  <Progress value={getProjectProgress(project)} className="h-2" />
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-[#27272A]">
                <div className="flex items-center gap-1 text-[#D4AF37]">
                  <DollarSign size={14} />
                  <span className="text-sm font-medium">{formatCurrency(project.total_value)}</span>
                </div>
                {project.remaining_balance > 0 && (
                  <span className="text-xs text-[#F59E0B]">
                    Balance: {formatCurrency(project.remaining_balance)}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredProjects.length === 0 && (
          <div className="col-span-full text-center py-12">
            <FolderKanban size={48} className="text-[#71717A] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No projects found</p>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#121214] border-[#27272A] max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA]">Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label className="text-[#A1A1AA]">Project Title</Label>
              <Input
                value={projectForm.title}
                onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Status</Label>
                <Select value={projectForm.status} onValueChange={(v) => setProjectForm({ ...projectForm, status: v })}>
                  <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                    {statusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Deadline</Label>
                <Input
                  type="date"
                  value={projectForm.deadline}
                  onChange={(e) => setProjectForm({ ...projectForm, deadline: e.target.value })}
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Deliverables</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newDeliverable}
                  onChange={(e) => setNewDeliverable(e.target.value)}
                  placeholder="Add deliverable"
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  onKeyPress={(e) => e.key === 'Enter' && addDeliverable()}
                />
                <Button onClick={addDeliverable} variant="outline" className="border-[#27272A]">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {projectForm.deliverables.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-[#1A1A1D] rounded-lg">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleDeliverable(idx)}>
                        {d.completed ? (
                          <CheckCircle2 size={18} className="text-[#22C55E]" />
                        ) : (
                          <Circle size={18} className="text-[#71717A]" />
                        )}
                      </button>
                      <span className={d.completed ? "text-[#71717A] line-through" : "text-[#FAFAFA]"}>
                        {d.name}
                      </span>
                    </div>
                    <button onClick={() => removeDeliverable(idx)} className="text-[#EF4444]">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Notes</Label>
              <Textarea
                value={projectForm.notes}
                onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })}
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpdateProject} className="flex-1 btn-gold">
                Save Changes
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleDeleteProject(selectedProject?.id)}
                className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
